// src/pages/ProductManagement/ProductOrdering.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../../api";
import "./ProductOrdering.css";

// This component assumes these endpoints exist:
//   GET  /api/products                          -> all TR products
//   GET  /api/products/category/:category       -> TR products for a category
//   GET  /api/english-products                  -> all EN products
//   GET  /api/english-products?category=...     -> EN products for a category (if not available, we fall back to filtering all EN client side)
//   POST /api/products/reorder                  -> { category, orders: [{ productId, newOrder }] }
//   POST /api/english-products/reorder          -> { category, orders: [{ productId, newOrder }] }

const cls = (...xs) => xs.filter(Boolean).join(" ");

async function apiGet(url) {
  const res = await api.get(url, { withCredentials: true });
  return res.data;
}

async function apiPost(url, body) {
  const res = await api.post(url, body, {
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
  });
  return res.data;
}

const SORT = {
  MANUAL: "Manual",
  DISPLAY: "Current display order",
  TITLE: "Title A-Z",
  CREATED: "Created date",
};

const PAGE_SIZES = [25, 50, 100];

function Row({
  item,
  index,
  disableReorder,
  onArrow,
  onJump,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}) {
  const [jump, setJump] = useState("");

  const handleJump = () => {
    const n = Number(jump);
    if (Number.isInteger(n) && n >= 0) onJump(item.uid, n);
  };

  return (
    <div
      className={cls(
        "po-row",
        item.langBoth
          ? "po-border-blue"
          : item.langTR
          ? "po-border-green"
          : "po-border-indigo"
      )}
      draggable={!disableReorder}
      onDragStart={(e) => onDragStart(e, item.uid)}
      onDragOver={(e) => onDragOver(e)}
      onDrop={(e) => onDrop(e, item.uid)}
      onDragEnd={onDragEnd}
      aria-grabbed="false"
      data-uid={item.uid}
      title={
        disableReorder
          ? "Reorder disabled for non-manual sort"
          : "Drag anywhere on the row"
      }
    >
      <div className="po-row-left" aria-hidden>
        ☰
      </div>

      <div className="po-row-main">
        <div className="po-row-title">
          <span className="po-row-name">{item.title || "Untitled"}</span>
          {item.langBoth && <span className="po-chip po-chip-blue">TR+EN</span>}
          {!item.langBoth && item.langTR && (
            <span className="po-chip po-chip-green">TR</span>
          )}
          {!item.langBoth && item.langEN && (
            <span className="po-chip po-chip-indigo">EN</span>
          )}
          <span className="po-chip">#{index}</span>
        </div>
        <div className="po-row-sub">
          {item.subtitle ? (
            <span className="po-subtext">{item.subtitle}</span>
          ) : null}
          <span className="po-id">{item.uid}</span>
        </div>
      </div>

      <div className="po-row-actions">
        <div className="po-jump">
          <input
            type="number"
            min={0}
            inputMode="numeric"
            pattern="[0-9]*"
            value={jump}
            onChange={(e) => setJump(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleJump();
            }}
            className="po-input"
            placeholder="New pos"
            aria-label="New position"
            disabled={disableReorder}
          />
          <button
            className="po-btn"
            onClick={handleJump}
            disabled={disableReorder}
            title="Move to position"
          >
            Move
          </button>
        </div>
        <div className="po-arrows">
          <button
            className="po-btn"
            onClick={() => onArrow(item.uid, -1)}
            disabled={disableReorder}
            aria-label="Move up"
            title="Move up"
          >
            ↑
          </button>
          <button
            className="po-btn"
            onClick={() => onArrow(item.uid, 1)}
            disabled={disableReorder}
            aria-label="Move down"
            title="Move down"
          >
            ↓
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ProductOrdering() {
  // Categories and selection
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("");

  // Sort, filters, pagination
  const [sort, setSort] = useState(SORT.DISPLAY);
  const [query, setQuery] = useState("");
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [page, setPage] = useState(0);
  const [showTR, setShowTR] = useState(true);
  const [showEN, setShowEN] = useState(false);
  const [langMode, setLangMode] = useState("tr"); // 'tr' | 'en'

  // Status
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  // Raw lists for selected category
  const [trList, setTrList] = useState([]);
  const [enList, setEnList] = useState([]);

  // Manual order for current filtered set
  const [manualOrder, setManualOrder] = useState([]);

  // Drag state
  const dragFrom = useRef(null);

  // 1) Load categories from TR products, union EN if available. Keep select enabled.
  useEffect(() => {
  let alive = true;

  const readCategory = (p) => {
    if (!p || typeof p !== 'object') return '';
    const c = p.category;
    if (typeof c === 'string') return c.trim();
    if (c && typeof c === 'object') {
      if (typeof c.slug === 'string' && c.slug.trim()) return c.slug.trim();
      if (typeof c.name === 'string' && c.name.trim()) return c.name.trim();
    }
    const alt = p.categorySlug || p.categoryName || p.kategori || p.kategoriSlug || p.kategoriName || p.cat;
    return typeof alt === 'string' ? alt.trim() : '';
  };

  (async () => {
    try {
      const bust = Date.now();
      const trAll = await apiGet(`/products?t=${bust}`).catch(() => []);
      const enAll = await apiGet(`/english-products?t=${bust}`).catch(() => []);

      const trCats = Array.isArray(trAll) ? trAll.map(readCategory).filter(Boolean) : [];
      const enCats = Array.isArray(enAll) ? enAll.map(readCategory).filter(Boolean) : [];

      const setCats = new Set([...trCats, ...enCats].map(s => s.toString()).filter(Boolean));
      const list = Array.from(setCats).sort((a, b) => a.localeCompare(b));

      if (!alive) return;
      setCategories(list);
      setCategory(prev => (prev && list.includes(prev)) ? prev : (list[0] || ''));
      setError(list.length ? '' : 'No categories found');
    } catch (err) {
      if (!alive) return;
      setCategories([]);
      setCategory('');
      setError('Failed to load categories from products');
    }
  })();

  return () => { alive = false; };
}, []);


  // 2) Load products for the selected category using the endpoints you actually have
  useEffect(() => {
    if (!category) return;
    setLoading(true);
    setError("");
    setPage(0);

    (async () => {
      try {
        const bust = Date.now();

        // TR by category
        const tr = await apiGet(
          `/products/category/${encodeURIComponent(category)}?t=${bust}`
        ).catch(() => []);

    // EN by category if supported, else filter client side
        let en = [];
        try {
          en = await apiGet(
            `/english-products?category=${encodeURIComponent(
              category
            )}&t=${bust}`
          );
        } catch {
          const enAll = await apiGet(`/english-products?t=${bust}`).catch(
            () => []
          );
          en = Array.isArray(enAll)
            ? enAll.filter(
                (p) =>
                  String(p.category || "").toLowerCase() ===
                  String(category).toLowerCase()
              )
            : [];
        }

        // In EN mode, include TR items that have hasEnglishTranslation === true
        if (langMode === "en") {
          const trWithTranslations = Array.isArray(tr)
            ? tr.filter((t) => t && t.hasEnglishTranslation === true)
            : [];
          setTrList(trWithTranslations);
        } else {
          setTrList(Array.isArray(tr) ? tr : []);
        }
        setEnList(Array.isArray(en) ? en : []);
      } catch (e) {
        console.error(e);
        setError("Failed to load products");
        setTrList([]);
        setEnList([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [category, langMode]);

  // 3) Build unified items for ordering
  const unified = useMemo(() => {
    const trById = new Map();
    trList.forEach((t) => trById.set(String(t._id || t.id), t));

    const pairs = new Map(); // turkishId -> { tr, en }
    const enOrphans = [];
    for (const e of enList) {
      const tid = e.turkishProductId ? String(e.turkishProductId) : null;
      if (tid && trById.has(tid)) {
        const slot = pairs.get(tid) || { tr: trById.get(tid), en: null };
        slot.en = e;
        pairs.set(tid, slot);
      } else {
        enOrphans.push(e);
      }
    }

    const items = [];

    // Paired rows
    pairs.forEach(({ tr, en }) => {
      const trId = String(tr._id || tr.id);
      const enId = String(en._id || en.id);
      const title = en?.title || tr?.title || "Untitled";
      const subtitle =
        tr?.title && en?.title && tr.title !== en.title
          ? `TR: ${tr.title} | EN: ${en.title}`
          : "";
      const orderTR = Number.isInteger(tr?.order)
        ? tr.order
        : Number.MAX_SAFE_INTEGER;
      const orderEN = Number.isInteger(en?.order)
        ? en.order
        : Number.MAX_SAFE_INTEGER;
      const createdAt = Math.min(
        new Date(tr?.createdAt || Date.now()).valueOf(),
        new Date(en?.createdAt || Date.now()).valueOf()
      );
      items.push({
        uid: `pair:${trId}`,
        title,
        subtitle,
        langTR: true,
        langEN: true,
        langBoth: true,
        trId: trId,
        enId: enId,
        orderHint: Math.min(orderTR, orderEN),
        createdAt,
      });
    });

    // TR only
    for (const t of trList) {
      const id = String(t._id || t.id);
      if (!pairs.has(id)) {
        items.push({
          uid: `tr:${id}`,
          title: t.title || "Untitled",
          subtitle: "",
          langTR: true,
          langEN: false,
          langBoth: false,
          trId: id,
          enId: null,
          orderHint: Number.isInteger(t.order)
            ? t.order
            : Number.MAX_SAFE_INTEGER,
          createdAt: new Date(t.createdAt || Date.now()).valueOf(),
          hasEnglishTranslation: t.hasEnglishTranslation === true,
        });
      }
    }

    // EN only
    for (const e of enOrphans) {
      const id = String(e._id || e.id);
      items.push({
        uid: `en:${id}`,
        title: e.title || "Untitled",
        subtitle: "",
        langTR: false,
        langEN: true,
        langBoth: false,
        trId: null,
        enId: id,
        orderHint: Number.isInteger(e.order)
          ? e.order
          : Number.MAX_SAFE_INTEGER,
        createdAt: new Date(e.createdAt || Date.now()).valueOf(),
      });
    }

    return items;
  }, [trList, enList]);

  // 4) Filters
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return unified.filter((it) => {
      if (langMode === "tr") {
        if (!it.langTR) return false;
      } else {
        const isEnVisible = it.langEN || (it.langTR && !it.langEN && it.hasEnglishTranslation === true);
        if (!isEnVisible) return false;
      }
      if (q) {
        const hay = `${it.title} ${it.subtitle} ${it.uid}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [unified, langMode, query]);

  // 5) Sort
  const sorted = useMemo(() => {
    // Base ordering according to selected sort
    let base = [...filtered];
    switch (sort) {
      case SORT.TITLE:
        base.sort((a, b) =>
          a.title.localeCompare(b.title, undefined, { sensitivity: "base" })
        );
        break;
      case SORT.CREATED:
        base.sort((a, b) => a.createdAt - b.createdAt);
        break;
      case SORT.DISPLAY:
        base.sort((a, b) => a.orderHint - b.orderHint);
        break;
      case SORT.MANUAL:
      default:
        // If Manual, we still apply the manual overlay below
        break;
    }

    // Apply manual overlay so user interactions are reflected visually
    if (manualOrder.length) {
      const pos = new Map();
      manualOrder.forEach((uid, i) => pos.set(uid, i));
      const withIndex = base.map((it, i) => ({ it, i }));
      withIndex.sort((a, b) => {
        const pa = pos.get(a.it.uid);
        const pb = pos.get(b.it.uid);
        const aa = pa === undefined ? Number.MAX_SAFE_INTEGER : pa;
        const bb = pb === undefined ? Number.MAX_SAFE_INTEGER : pb;
        if (aa !== bb) return aa - bb;
        return a.i - b.i; // keep prior relative order when no manual pos
      });
      base = withIndex.map((x) => x.it);
    } else if (sort === SORT.MANUAL) {
      // In Manual with no manualOrder yet, fall back to display order
      base.sort((a, b) => a.orderHint - b.orderHint);
    }

    return base;
  }, [filtered, sort, manualOrder]);

  // 6) Ensure manual order is ready when needed (initialize when entering Manual or when empty)
  useEffect(() => {
    if (sort !== SORT.MANUAL && manualOrder.length) return;
    const known = new Set(manualOrder);
    const needsInit =
      sorted.some((it) => !known.has(it.uid)) ||
      manualOrder.length !== sorted.length;
    if (needsInit) setManualOrder(sorted.map((it) => it.uid));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorted, sort]);

  // Keep showTR/showEN in sync with the toggle mode (no 'both')
  useEffect(() => {
    if (langMode === "tr") {
      setShowTR(true);
      setShowEN(false);
    } else {
      setShowTR(false);
      setShowEN(true);
    }
  }, [langMode]);

  // When showing Current display order, ensure we reflect backend/category page order exactly
  // Clear any manual overlay whenever data set, category, or language view changes while in DISPLAY
  useEffect(() => {
    if (sort === SORT.DISPLAY) {
      setManualOrder([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, category, langMode, trList, enList]);

  // 7) Pagination
  const total = sorted.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const pageSafe = Math.min(page, pageCount - 1);
  const start = pageSafe * pageSize;
  const end = Math.min(total, start + pageSize);
  const paged = sorted.slice(start, end);

  const disableReorder = false;

  // 8) Reorder helpers
  function moveBy(uid, delta) {
    setManualOrder((current) => {
      const base = current.length ? current : sorted.map((it) => it.uid);
      const i = base.indexOf(uid);
      if (i < 0) return base;
      const j = Math.max(0, Math.min(base.length - 1, i + delta));
      if (i === j) return base;
      const next = base.slice();
      const [it] = next.splice(i, 1);
      next.splice(j, 0, it);
      return next;
    });
  }

  function jumpTo(uid, newIndex) {
    setManualOrder((current) => {
      const base = current.length ? current : sorted.map((it) => it.uid);
      const i = base.indexOf(uid);
      if (i < 0) return base;
      const j = Math.max(0, Math.min(base.length - 1, newIndex));
      if (i === j) return base;
      const next = base.slice();
      const [it] = next.splice(i, 1);
      next.splice(j, 0, it);
      return next;
    });
  }

  function onDragStart(e, uid) {
    dragFrom.current = uid;
    e.dataTransfer.effectAllowed = "move";
  }

  function onDragOver(e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }

  function onDrop(e, toUid) {
    e.preventDefault();
    const fromUid = dragFrom.current;
    dragFrom.current = null;
    if (!fromUid || fromUid === toUid) return;
    setManualOrder((current) => {
      const base = current.length ? current : sorted.map((it) => it.uid);
      const from = base.indexOf(fromUid);
      const to = base.indexOf(toUid);
      if (from < 0 || to < 0) return base;
      const next = base.slice();
      const [it] = next.splice(from, 1);
      next.splice(to, 0, it);
      return next;
    });
  }

  function onDragEnd() {
    dragFrom.current = null;
  }

  // 9) Save split by collection for current category
  async function handleSave() {
    try {
      setSaving(true);
      setSaveMsg("");

      const pos = new Map();
      manualOrder.forEach((id, i) => pos.set(id, i));
      const ordered = [...filtered].sort(
        (a, b) => (pos.get(a.uid) ?? 0) - (pos.get(b.uid) ?? 0)
      );

      const trUpdates = [];
      const enUpdates = [];
      let idx = 0;
      for (const item of ordered) {
        if (item.langBoth) {
          trUpdates.push({ productId: item.trId, newOrder: idx });
          enUpdates.push({ productId: item.enId, newOrder: idx });
          idx += 1;
        } else if (item.langTR && !item.langEN) {
          trUpdates.push({ productId: item.trId, newOrder: idx });
          idx += 1;
        } else if (item.langEN && !item.langTR) {
          enUpdates.push({ productId: item.enId, newOrder: idx });
          idx += 1;
        }
      }

      const reqs = [];
      if (trUpdates.length)
        reqs.push(
          apiPost("/products/reorder", { category, orders: trUpdates })
        );
      if (enUpdates.length)
        reqs.push(
          apiPost("/english-products/reorder", {
            category,
            orders: enUpdates,
          })
        );

      const results = await Promise.allSettled(reqs);
      const anyRejected = results.some((r) => r.status === "rejected");

      if (anyRejected) {
        setSaveMsg("Saved with partial errors. Check network and retry.");
      } else {
        setSaveMsg("Saved");
        // Refresh current category view
        const [tr, enMaybe, enAll] = await Promise.all([
          apiGet(
            `/products/category/${encodeURIComponent(
              category
            )}?t=${Date.now()}`
          ),
          apiGet(
            `/english-products?category=${encodeURIComponent(
              category
            )}&t=${Date.now()}`
          ).catch(() => null),
          apiGet(`/english-products?t=${Date.now()}`).catch(() => []),
        ]);
        const en = Array.isArray(enMaybe)
          ? enMaybe
          : Array.isArray(enAll)
          ? enAll.filter(
              (p) =>
                String(p.category || "").toLowerCase() ===
                String(category).toLowerCase()
            )
          : [];
        setTrList(Array.isArray(tr) ? tr : []);
        setEnList(Array.isArray(en) ? en : []);
      }
    } catch (e) {
      console.error(e);
      setSaveMsg("Save failed");
    } finally {
      setSaving(false);
    }
  }

  function handleResetInputs() {
    setManualOrder(sorted.map((it) => it.uid));
    setSaveMsg("");
  }

  return (
    <div className="po-wrap">
      <div className="po-header">
        <h1 className="po-title">Product Ordering</h1>
        <div className="po-controls">
          <label className="po-field">
            <span>Category</span>
            <select
              className="po-select"
              value={category || categories[0] || ""}
              onChange={(e) => setCategory(e.target.value)}
              disabled={!categories || categories.length === 0}
            >
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>

          <label className="po-field">
            <span>Sort</span>
            <select
              className="po-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
            >
              {Object.values(SORT).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>

          <label className="po-field">
            <span>Page size</span>
            <select
              className="po-select"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>

          <div className="po-actions">
            <button
              className="po-btn po-primary"
              onClick={handleSave}
              disabled={saving || loading}
              title="Save order"
            >
              {saving ? "Saving..." : "Save"}
            </button>
            <button
              className="po-btn"
              onClick={handleResetInputs}
              disabled={loading}
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      <div className="po-filters">
        <input
          className="po-input po-search"
          placeholder="Search title or id"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(0);
          }}
        />
        <div className="po-toggle-group" role="tablist" aria-label="Language filter">
          <button
            type="button"
            className={cls("po-toggle", langMode === "tr" && "po-toggle-on")}
            onClick={() => setLangMode("tr")}
            role="tab"
            aria-selected={langMode === "tr"}
          >
            TR
          </button>
          <button
            type="button"
            className={cls("po-toggle", langMode === "en" && "po-toggle-on")}
            onClick={() => setLangMode("en")}
            role="tab"
            aria-selected={langMode === "en"}
          >
            EN
          </button>
        </div>
        {/* Reordering is always enabled now */}
      </div>

      {error && <div className="po-error">{error}</div>}
      {saveMsg && <div className="po-note">{saveMsg}</div>}

      <div className="po-list">
        {loading ? (
          <div className="po-skeleton">Loading...</div>
        ) : paged.length ? (
          paged.map((item, i) => (
            <Row
              key={item.uid}
              item={item}
              index={start + i}
              disableReorder={false}
              onArrow={(uid, d) => moveBy(uid, d)}
              onJump={(uid, n) => jumpTo(uid, n)}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDrop={onDrop}
              onDragEnd={onDragEnd}
            />
          ))
        ) : (
          <div className="po-empty">No products</div>
        )}
      </div>

      <div className="po-footer">
        <div className="po-count">{total} items</div>
        <div className="po-pager">
          <button
            className="po-btn"
            onClick={() => setPage(0)}
            disabled={pageSafe === 0}
          >
            {"<<"}
          </button>
          <button
            className="po-btn"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={pageSafe === 0}
          >
            {"<"}
          </button>
          <span className="po-page">
            Page {pageSafe + 1} of {pageCount}
          </span>
          <button
            className="po-btn"
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            disabled={pageSafe >= pageCount - 1}
          >
            {">"}
          </button>
          <button
            className="po-btn"
            onClick={() => setPage(pageCount - 1)}
            disabled={pageSafe >= pageCount - 1}
          >
            {">>"}
          </button>
        </div>
      </div>
    </div>
  );
}
